import * as THREE from "three";
import { STEP_INFO, TRAINER_STEPS, stepIndex, type TrainerStep } from "./trainer/steps";

export interface Hud {
  mesh: THREE.Mesh;
  /** Refresh copy for the current trainer stage. */
  setStep(step: TrainerStep): void;
}

/**
 * World-space stage panel (canvas texture). Readable at Quest distances;
 * redraws only when the trainer step changes.
 */
export function createHud(): Hud {
  const canvas = document.createElement("canvas");
  canvas.width = 512;
  canvas.height = 288;
  const ctx = canvas.getContext("2d")!;
  const texture = new THREE.CanvasTexture(canvas);
  texture.colorSpace = THREE.SRGBColorSpace;

  const mesh = new THREE.Mesh(
    new THREE.PlaneGeometry(0.95, 0.54),
    new THREE.MeshBasicMaterial({ map: texture, transparent: true }),
  );
  mesh.name = "trainer-hud";

  function draw(step: TrainerStep): void {
    const info = STEP_INFO[step];
    const done = step === "complete";
    const index = stepIndex(step);
    const total = TRAINER_STEPS.length;

    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.fillStyle = "rgba(15, 23, 42, 0.92)";
    roundRect(ctx, 8, 8, canvas.width - 16, canvas.height - 16, 20);
    ctx.fill();
    ctx.strokeStyle = done ? "rgba(53, 196, 106, 0.65)" : "rgba(102, 204, 255, 0.55)";
    ctx.lineWidth = 4;
    ctx.stroke();

    ctx.fillStyle = "#94a3b8";
    ctx.font = "600 22px system-ui, sans-serif";
    ctx.textBaseline = "top";
    ctx.fillText("OUTLET ASSEMBLY  ·  NON-ENERGIZED MOCKUP", 36, 28);

    ctx.fillStyle = "#e2e8f0";
    ctx.font = "700 40px system-ui, sans-serif";
    ctx.fillText(info.label, 36, 64);

    ctx.fillStyle = "#cbd5e1";
    ctx.font = "500 24px system-ui, sans-serif";
    wrapText(ctx, info.prompt, 36, 120, canvas.width - 72, 30);

    // Stage dots
    const dotY = 210;
    const gap = 28;
    const startX = 36;
    for (let i = 0; i < total; i++) {
      const x = startX + i * gap;
      ctx.beginPath();
      ctx.arc(x + 8, dotY, 7, 0, Math.PI * 2);
      if (i < index || done) {
        ctx.fillStyle = "#35c46a";
      } else if (i === index) {
        ctx.fillStyle = "#66ccff";
      } else {
        ctx.fillStyle = "rgba(148,163,184,0.35)";
      }
      ctx.fill();
    }

    ctx.fillStyle = "#94a3b8";
    ctx.font = "500 20px system-ui, sans-serif";
    ctx.fillText("Grab: squeeze / click  ·  Reset: R (nearest) or plate", 36, 240);

    texture.needsUpdate = true;
  }

  draw("place_wires");

  return { mesh, setStep: draw };
}

function wrapText(
  ctx: CanvasRenderingContext2D,
  text: string,
  x: number,
  y: number,
  maxWidth: number,
  lineHeight: number,
): void {
  const words = text.split(" ");
  let line = "";
  let yy = y;
  for (const word of words) {
    const test = line ? `${line} ${word}` : word;
    if (ctx.measureText(test).width > maxWidth && line) {
      ctx.fillText(line, x, yy);
      line = word;
      yy += lineHeight;
    } else {
      line = test;
    }
  }
  if (line) ctx.fillText(line, x, yy);
}

function roundRect(
  ctx: CanvasRenderingContext2D,
  x: number,
  y: number,
  w: number,
  h: number,
  r: number,
): void {
  const radius = Math.min(r, w / 2, h / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.arcTo(x + w, y, x + w, y + h, radius);
  ctx.arcTo(x + w, y + h, x, y + h, radius);
  ctx.arcTo(x, y + h, x, y, radius);
  ctx.arcTo(x, y, x + w, y, radius);
  ctx.closePath();
}
